import React, { useState, useEffect } from "react";
import {
  Button,
  tokens,
  Typography,
  LoadingOverlay,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import { useMediaQuery } from "react-responsive";
import {
  Card,
  CardHeader as BaseCardHeader,
  CardContent,
  CardSectionDivider,
} from "../molecules/Card";
import {
  groupDesignPreferences,
  getFieldKey,
  getInitialValue,
} from "../../utils/groupDesignPreferences";
import {
  getDesignPreferenceValue,
  isEffectivelyRequired,
} from "../../utils/designPreferenceConditions";
import { DesignPreferenceCard } from "../molecules/DesignPreferenceCard";
import { ConfirmModal } from "../molecules/ConfirmModal";
import { UpdateDesignPreferencesRequestBody } from "../../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import type { DesignPreferenceItemFormatted } from "../../types/designPreferences";
import { ErrorModal } from "../../../components/molecules/ErrorModal";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useCheckoutFooter } from "checkout/providers/CheckoutFooterContext";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";
import { useDesignPreferences } from "checkout/hooks/getDesignPreferences";
import { updateDesignPreferences } from "services/prospect";
import { useAuthToken } from "hooks/useAuthToken";
import { ViewMode } from "checkout/types/payments-ui-form";
import { HyperlinkButton } from "components/atoms/HyperlinkButton";

const ConfirmPreferencesTaskView: React.FC = () => {
  const hybridToken = useAuthToken("HYBRID")?.token ?? "";
  const { prospectId, navigateToNextTask, refreshTasks } = useCheckoutContext();

  const [loadingSubmitDesignPreferences, setLoadingSubmitDesignPreferences] =
    useState(false);
  const [submitDesignPreferencesError, setSubmitDesignPreferencesError] =
    useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("view");

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [formValues, setFormValues] = useState<Map<string, string>>(new Map());
  const [editedFieldQuestions, setEditedFieldQuestions] = useState<Set<string>>(
    new Set()
  );

  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });

  const {
    designPreferencesData,
    designPreferencesError,
    designPreferencesLoading,
    refetch,
  } = useDesignPreferences(Boolean(prospectId), prospectId);

  const designPreferences = designPreferencesData?.designPreferences;

  const proposalId = designPreferences?.proposalId;
  const opptyName = designPreferences?.opptyName;
  const agreementType = designPreferences?.agreementType;
  const conId = designPreferences?.conId || "";

  const newDesignPref = (designPreferences?.newDesignPref ??
    []) as DesignPreferenceItemFormatted[];

  const preferenceGroups = groupDesignPreferences(newDesignPref);

  const itemIndexMap = new Map<DesignPreferenceItemFormatted, number>();
  newDesignPref.forEach((item, index) => {
    itemIndexMap.set(item, index);
  });

  const getValueForDesignPreference = (
    item: DesignPreferenceItemFormatted
  ): string =>
    getDesignPreferenceValue(item, viewMode, itemIndexMap, formValues);

  const allRequiredFieldsFilled =
    newDesignPref.length > 0 &&
    newDesignPref
      .filter((item) =>
        isEffectivelyRequired(item, newDesignPref, getValueForDesignPreference)
      )
      .every((field) => getValueForDesignPreference(field).trim() !== "");

  const buildDesignPreferencesBody =
    (): UpdateDesignPreferencesRequestBody | null => {
      if (!newDesignPref.length) return null;

      const payloadItems = newDesignPref
        .map((item) => {
          const originalIndex = itemIndexMap.get(item);
          if (originalIndex === undefined) {
            return null;
          }
          const key = getFieldKey(item, originalIndex);
          const formValue = formValues.get(key);

          let response: string | null;
          if (formValue !== undefined) {
            if (formValue === "") {
              response = item.response === null ? null : "";
            } else {
              const isTextInput =
                item.respDataType === "Text" ||
                item.respDataType === "Phone" ||
                item.respDataType === "Text Area";
              response = isTextInput ? formValue.trim() : formValue;
            }
          } else {
            response = item.response;
          }

          const baseObject = {
            question: item.question || "",
            response: response ?? "",
            opptyId: item.opptyId || "",
            doNotTrack: item.doNotTrack || false,
            isRequired: isEffectivelyRequired(item, newDesignPref, (i) => {
              const idx = itemIndexMap.get(i);
              if (idx === undefined) {
                return i.response ?? "";
              }
              const k = getFieldKey(i, idx);
              return formValues.get(k) ?? i.response ?? "";
            }),
            parentQuestion: item.parentQuestion || null,
            recId: item.recId || null,
            featureType: item.featureType || null,
            contactId: item.contactId || null,
          };

          return baseObject;
        })
        .filter((item) => item !== null) as any[];

      return {
        ProspectId: prospectId,
        designPreferences: {
          conId: conId,
          newDesignPref: payloadItems,
        },
      };
    };

  const submitDesignPreferences = async (
    requestBody: UpdateDesignPreferencesRequestBody
  ) => {
    setLoadingSubmitDesignPreferences(true);
    try {
      const response = await updateDesignPreferences(
        hybridToken,
        prospectId,
        requestBody
      );
      if (response.response.Status === "Success") {
        await refetch();
      } else {
        throw response;
      }
    } catch (error: any) {
      console.log(
        "error when attempting to submit/update design preferences:",
        error.response?.Message
      );
      setSubmitDesignPreferencesError(true);
    } finally {
      setLoadingSubmitDesignPreferences(false);
    }
  };

  useEffect(() => {
    if (viewMode === "edit") {
      const initialValues = new Map<string, string>();
      newDesignPref.forEach((item, index) => {
        if (
          item.respDataType !== "Header" &&
          item.respDataType !== "SubHeader"
        ) {
          const key = getFieldKey(item, index);
          initialValues.set(key, getInitialValue(item));
        }
      });
      setFormValues(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- viewMode is the only dependency
  }, [viewMode]);

  useEffect(() => {
    setHasUnsavedChanges(editedFieldQuestions.size > 0);
  }, [editedFieldQuestions]);

  const handleFieldChange = (
    item: DesignPreferenceItemFormatted,
    index: number,
    value: string
  ) => {
    const key = getFieldKey(item, index);
    setFormValues((prev) => {
      const newMap = new Map(prev);
      newMap.set(key, value);
      return newMap;
    });

    setEditedFieldQuestions((prev) => {
      const newSet = new Set(prev);
      const originalValue = getInitialValue(item);
      const currentValue = value;

      if (currentValue === originalValue) {
        newSet.delete(item.question);
      } else {
        newSet.add(item.question);
      }
      return newSet;
    });
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelModal(true);
    } else {
      setViewMode("view");
      setFormValues(new Map());
      setEditedFieldQuestions(new Set());
    }
  };

  const handleDiscardChanges = () => {
    setShowCancelModal(false);
    setViewMode("view");
    setHasUnsavedChanges(false);
    setEditedFieldQuestions(new Set());
    if (newDesignPref.length > 0) {
      const initialValues = new Map<string, string>();
      newDesignPref.forEach((item, index) => {
        if (
          item.respDataType !== "Header" &&
          item.respDataType !== "SubHeader"
        ) {
          const key = getFieldKey(item, index);
          initialValues.set(key, getInitialValue(item));
        }
      });
      setFormValues(initialValues);
    }
  };

  const handleKeepEditing = () => {
    setShowCancelModal(false);
  };

  const handleEdit = () => {
    if (newDesignPref.length > 0) {
      const initialValues = new Map<string, string>();
      newDesignPref.forEach((item, index) => {
        if (
          item.respDataType !== "Header" &&
          item.respDataType !== "SubHeader"
        ) {
          const key = getFieldKey(item, index);
          initialValues.set(key, getInitialValue(item));
        }
      });
      setFormValues(initialValues);
    }
    setEditedFieldQuestions(new Set());
    setViewMode("edit");
  };

  const handleSave = async () => {
    if (!allRequiredFieldsFilled) {
      return;
    }
    const requestBody = buildDesignPreferencesBody();
    if (!requestBody) {
      console.error("Failed to build design preferences request body");
      return;
    }

    await submitDesignPreferences(requestBody);

    setViewMode("view");
    setHasUnsavedChanges(false);
    setFormValues(new Map());
    setEditedFieldQuestions(new Set());
  };

  const handleDismissError = () => {
    setSubmitDesignPreferencesError(false);
  };

  const DesignPreferencesInfo = () => {
    return (
      <Card>
        <StyledCardHeader isMobile={isMobile}>
          <Typography
            lineHeight={24}
            size={tokens.FONT_SIZE_3}
            color={tokens.BRAND_HEROBLUE}
          >
            Design preferences
          </Typography>
        </StyledCardHeader>
        <CardSectionDivider />
        <CardContent>
          <InfoFields>
            <InfoField>
              <Typography color={tokens.TINTS_OFF_WHITE_20}>
                Opportunity
              </Typography>
              <Typography>{opptyName ?? ""}</Typography>
            </InfoField>
            <InfoField>
              <Typography color={tokens.TINTS_OFF_WHITE_20}>
                Proposal
              </Typography>
              <Typography>{proposalId ?? ""}</Typography>
            </InfoField>
            <InfoField>
              <Typography color={tokens.TINTS_OFF_WHITE_20}>
                Agreement type
              </Typography>
              <Typography>{agreementType ?? ""}</Typography>
            </InfoField>
          </InfoFields>
        </CardContent>
      </Card>
    );
  };

  const showFooter =
    !designPreferencesLoading && !loadingSubmitDesignPreferences;

  useCheckoutFooter(
    showFooter ? (
      <StyledFooter>
        {viewMode === "edit" ? (
          <>
            <StyledButton color="secondary" onClick={handleCancel}>
              Cancel
            </StyledButton>
            <StyledButton
              color="primary"
              disabled={!hasUnsavedChanges || !allRequiredFieldsFilled}
              onClick={handleSave}
            >
              Save
            </StyledButton>
          </>
        ) : (
          <>
            <StyledButton
              disabled={newDesignPref.length === 0}
              onClick={handleEdit}
              color="secondary"
            >
              Edit
            </StyledButton>
            <StyledButton
              color="primary"
              disabled={!allRequiredFieldsFilled}
              onClick={navigateToNextTask}
            >
              Continue
            </StyledButton>
          </>
        )}
      </StyledFooter>
    ) : null,
    [
      showFooter,
      handleCancel,
      handleSave,
      handleEdit,
      navigateToNextTask,
      viewMode,
      hasUnsavedChanges,
      allRequiredFieldsFilled,
      newDesignPref.length,
    ]
  );

  if (designPreferencesLoading || loadingSubmitDesignPreferences) {
    return (
      <StyledLoadingOverlay
        data-testid="loading-overlay"
        message={
          loadingSubmitDesignPreferences
            ? "Saving your information…"
            : "Loading..."
        }
      />
    );
  }

  return (
    <>
      {viewMode === "edit" ? (
        <StyledTypography testId="edit-mode-description">
          Confirming customer preferences helps us make the best decision for
          your home. After saving you will need to refresh this page to see the
          updated status of this checkout step.
        </StyledTypography>
      ) : (
        <StyledTypography testId="view-mode-description">
          Confirming customer preferences helps us make the best decision for
          your home. After saving you will need to{" "}
          <HyperlinkButton
            onClick={() => {
              refetch();
              refreshTasks();
            }}
          >
            refresh this page
          </HyperlinkButton>{" "}
          to see the updated status of this checkout step.
        </StyledTypography>
      )}
      <ContentContainer>
        <DesignPreferencesInfo />

        {preferenceGroups.map((group, index) => (
          <DesignPreferenceCard
            key={index}
            group={group}
            viewMode={viewMode}
            formValues={formValues}
            onFieldChange={handleFieldChange}
            fieldIndices={itemIndexMap}
          />
        ))}
      </ContentContainer>

      {(designPreferencesLoading || loadingSubmitDesignPreferences) && (
        <StyledLoadingOverlay
          data-testid="loading-overlay"
          message={
            loadingSubmitDesignPreferences
              ? "Saving your information…"
              : "Loading..."
          }
        />
      )}
      {(designPreferencesError || submitDesignPreferencesError) && (
        <ErrorModal
          action="Fetching Design Preferences Data"
          onRetry={refetch}
          context="ConfirmPreferencesCheckout"
          error={designPreferencesError}
          onClose={handleDismissError}
        />
      )}
      {showCancelModal && hasUnsavedChanges && (
        <ConfirmModal
          title="Are you sure you want to exit without saving?"
          cancelLabel="Cancel edits"
          onClose={handleDiscardChanges}
          onConfirm={handleKeepEditing}
          confirmLabel="Keep editing"
          modalType="warning"
          onCloseX={handleKeepEditing}
        >
          <Typography>
            All of your unsaved changes will be discarded.
          </Typography>
        </ConfirmModal>
      )}
    </>
  );
};

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 56px;

  @media (max-width: ${mobileMaxWidth}px) {
    margin-bottom: 48px;
  }
`;

const StyledTypography = styled(Typography)`
  padding-bottom: 32px !important;
`;

const StyledButton = styled(Button)`
  padding: 12px 32px;
  width: fit-content;

  @media (max-width: ${mobileMaxWidth}px) {
    padding: 18px 24px;
    width: 100%;
  }

  &.sr-button-md.sr-button-secondary {
    padding: 11px 32px;

    @media (max-width: ${mobileMaxWidth}px) {
      padding: 17px 24px;
      width: 100%;
    }
  }
`;

const StyledFooter = styled.footer`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;

  @media (max-width: ${mobileMaxWidth}px) {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
  }
`;

const InfoFields = styled.div<{ cols?: number }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @media (max-width: ${mobileMaxWidth}px) {
    flex-direction: column;
  }
`;

const InfoField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StyledCardHeader = styled(BaseCardHeader)<{ isMobile?: boolean }>`
  margin: 20px 24px;
  ${({ isMobile }) =>
    isMobile &&
    `
    flex-direction: column;
    align-items: flex-start;
  `}
`;

const StyledLoadingOverlay = styled(LoadingOverlay)`
  & > h2 {
    text-align: center;
  }
`;

export { ConfirmPreferencesTaskView };
